import { motion } from "framer-motion"
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react"
import { useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { chatItemId } from "../utils/parsers/messages"
import ActiveChips from "./ActiveChips"
import BundleChatItem from "./BundleChatItem"
import ChatListSkeleton from "./ChatListSkeleton"
import { CHAT_TABS, MOTION_VARIANTS, TAB_LABELS, TAB_ORDER } from "./constants"
import FilterPanel, { FilterTrigger, countActiveFilters } from "./FilterPanel"
import LeadChatItem from "./LeadChatItem"

const GlobalTooltip = ({ tooltip }) => {
  if (!tooltip) return null
  return createPortal(
    <div
      className="fixed z-[9999] w-72 max-w-xs pointer-events-none"
      style={{ top: tooltip.top, left: tooltip.left, transform: "translateY(-50%)" }}
    >
      <div className="flex items-center">
        <div className="w-2 h-2 bg-slate-800 rotate-45 flex-shrink-0 -mr-1" />
        <div
          className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg break-words leading-relaxed"
          dangerouslySetInnerHTML={{ __html: tooltip.message }}
        />
      </div>
    </div>,
    document.body
  )
}

// ---------------------------------------------------------------------------
// Единая точка входа — выбирает нужный компонент по табу
// ---------------------------------------------------------------------------
const ChatItem = ({ activeTab, presenceMap, isAdmin, onDeleteChat, onRestoreChat, ...props }) => {
  const chatType = activeTab === CHAT_TABS.LEADS ? "lead" : "bundle"
  const id = chatItemId(props.chat)
  const presenceUsers = id != null ? presenceMap?.[`${chatType}_${id}`] : undefined

  const { selectedChat, chat } = props
  const isSelected =
    !!selectedChat &&
    id != null &&
    selectedChat.id === chat.id &&
    chatItemId(selectedChat) === id

  const Item = activeTab === CHAT_TABS.LEADS ? LeadChatItem : BundleChatItem

  return (
    <div className="relative">
      <Item
        {...props}
        activeTab={activeTab}
        presenceUsers={presenceUsers}
        isAdmin={isAdmin}
        onDelete={onDeleteChat && chat.can_delete && !chat.archived ? () => onDeleteChat(chat) : undefined}
        onRestore={onRestoreChat && chat.archived ? () => onRestoreChat(chat) : undefined}
      />
      {/* Оверлей выбранного чата: не зависит от собственных фонов внутри Lead/BundleChatItem */}
      {isSelected && (
        <div
          aria-hidden
          className="absolute inset-0 z-10 pointer-events-none bg-blue-500/10 ring-1 ring-inset ring-blue-300"
        >
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" />
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ChatListSidebar
// ---------------------------------------------------------------------------
const ChatListSidebar = ({
  sidebarRef,
  isMobileView,
  selectedChat,
  isMobile,
  sidebarWidth,
  filteredChats,
  filters,
  onFilterChange,
  onFilterReset,
  onFilterSearch,
  activeTab,
  setActiveTab,
  onSelectChat,
  onClose,
  onStartResize,
  isAdmin,
  departments,
  sentinelRef,
  isLoadingMore,
  hasMore,
  onCollapse,
  onExpand,
  totalUnread,
  leadsUnread,
  aiUnread,
  messagethemes,
  presenceMap,
  currentUserId,
  aiSearchEnabled,
  onDeleteChat,
  onRestoreChat,
  onToggleArchive,
  onNewChat,
}) => {
  const { t } = useTranslation()
  const [showFilters, setShowFilters] = useState(false)
  const [tooltip, setTooltip] = useState(null)

  const activeCount = countActiveFilters(filters, activeTab)
  const isCollapsed = sidebarWidth === 0

  const isLeadsTab = activeTab === CHAT_TABS.LEADS
  const searchValue = isLeadsTab ? filters.lead_id || "" : filters.bundle_id || ""
  const searchField = isLeadsTab ? "lead_id" : "bundle_id"
  const searchPlaceholder = isLeadsTab
    ? t("admin.search_lead_id", "Lead ID")
    : t("admin.search_bundle_id", "Search")

  const isInitialLoading = isLoadingMore && filteredChats.length === 0

  if (isCollapsed) {
    return (
      <div className="relative w-0 flex-shrink-0">
        <button
          onClick={onExpand}
          className="absolute top-1/2 -translate-y-1/2 left-0 z-20 flex items-center justify-center w-5 h-10 bg-white border border-slate-200 border-l-0 rounded-r-lg shadow-md hover:bg-blue-50 hover:border-blue-300 transition-colors group"
          title={t("admin.show_chat_list", "Показать список чатов")}
        >
          <ChevronRight className="w-3 h-3 text-slate-400 group-hover:text-blue-500 transition-colors" />
        </button>
      </div>
    )
  }

  return (
    <div
      className={`${isMobileView && selectedChat ? "hidden" : "flex"} relative flex-shrink-0`}
      style={{
        width: isMobile ? "100%" : `${sidebarWidth}%`,
        minWidth: isMobile ? "100%" : "200px",
        maxWidth: isMobile ? "100%" : "600px",
      }}
    >
      {!isMobile && onCollapse && (
        <button
          onClick={onCollapse}
          className="absolute top-1/2 -translate-y-1/2 -right-[10px] flex items-center justify-center w-5 h-10 bg-white border border-slate-200 border-l-0 rounded-r-lg shadow-md hover:bg-blue-50 hover:border-blue-300 transition-colors z-40"
          title={t("admin.hide_chat_list")}
        >
          <ChevronLeft className="w-3 h-3 text-slate-400 hover:text-blue-500 transition-colors" />
        </button>
      )}

      <motion.div
        ref={sidebarRef}
        className="flex flex-col bg-white border-r border-slate-200 relative w-full overflow-hidden"
        variants={MOTION_VARIANTS.sidebar}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-3 border-b border-slate-200 flex-shrink-0">
          <FilterTrigger onClick={() => setShowFilters(true)} activeCount={activeCount} />
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              inputMode="numeric"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => {
                const value = e.target.value
                if (/^\d*$/.test(value)) onFilterChange(searchField, value)
              }}
              className="w-full pl-9 pr-3 py-1.5 bg-slate-100 rounded-full text-sm focus:outline-none focus:bg-white focus:ring-1 focus:ring-blue-500 transition-all"
            />
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-red-50 text-slate-500 hover:text-red-500 rounded-full transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <ActiveChips
          filters={filters}
          activeTab={activeTab}
          onFilterChange={onFilterChange}
          onFilterReset={onFilterReset}
          departments={departments}
          t={t}
        />

        {/* Tabs */}
        <div className="flex border-b border-slate-100 flex-shrink-0 overflow-x-auto scrollbar-none">
          {TAB_ORDER.filter((tabKey) => tabKey !== CHAT_TABS.AI_BOT || aiSearchEnabled).map(
            (tabKey) => {
              const isActive = activeTab === tabKey
              const badge =
                tabKey === CHAT_TABS.ALL && totalUnread > 0
                  ? totalUnread
                  : tabKey === CHAT_TABS.LEADS && leadsUnread > 0
                    ? leadsUnread
                    : tabKey === CHAT_TABS.AI_BOT && aiUnread > 0
                      ? aiUnread
                      : null
              return (
                <button
                  key={tabKey}
                  onClick={() => setActiveTab(tabKey)}
                  className={`flex-shrink-0 px-4 py-2.5 text-sm font-medium transition-colors relative whitespace-nowrap ${
                    isActive
                      ? "text-blue-500 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-blue-500"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <span className="flex items-center justify-center gap-1.5">
                    {t(TAB_LABELS[tabKey], tabKey)}
                    {badge && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-white text-[11px] font-semibold leading-none">
                        {badge > 99 ? "99+" : badge}
                      </span>
                    )}
                  </span>
                </button>
              )
            }
          )}
        </div>

        {/* Chat list */}
        <div className="flex-1 overflow-y-auto">
          {isInitialLoading ? (
            // Первая загрузка — показываем скелетон вместо "ничего не найдено"
            <ChatListSkeleton />
          ) : filteredChats.length === 0 ? (
            <div className="flex items-center justify-center h-24 text-sm text-slate-400">
              {t("admin.nothing_found")}
            </div>
          ) : (
            <>
              {filteredChats.map((chat, i) => (
                <ChatItem
                  key={`${chat.id}-${chatItemId(chat)}`}
                  chat={chat}
                  index={i}
                  selectedChat={selectedChat}
                  onSelectChat={onSelectChat}
                  t={t}
                  onShowTooltip={setTooltip}
                  onHideTooltip={() => setTooltip(null)}
                  departments={departments}
                  activeTab={activeTab}
                  presenceMap={presenceMap}
                  isAdmin={isAdmin}
                  onDeleteChat={onDeleteChat}
                  onRestoreChat={onRestoreChat}
                />
              ))}
              <GlobalTooltip tooltip={tooltip} />

              {hasMore && (
                <div ref={sentinelRef} className="flex justify-center py-4">
                  {isLoadingMore && (
                    <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  )}
                </div>
              )}
              {!hasMore && filteredChats.length > 0 && (
                <div className="text-center text-xs text-slate-400 py-3">
                  {t("admin.all_chats_are_loaded")}
                </div>
              )}
            </>
          )}
        </div>

        {onToggleArchive && (
          <div className="flex-shrink-0 flex items-center justify-between border-t border-slate-200">
            {onNewChat && !filters.archived && (
              <button
                type="button"
                onClick={onNewChat}
                className="px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50"
              >
                + {t("admin.ai_chat_bot_new_chat", "New chat")}
              </button>
            )}
            <button
              type="button"
              onClick={onToggleArchive}
              className="ml-auto px-3 py-2 text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700"
            >
              {filters.archived
                ? `← ${t("admin.ai_chat_bot_back_to_chats", "Back to chats")}`
                : t("admin.ai_chat_bot_archive", "Deleted chats")}
            </button>
          </div>
        )}

        {!isMobile && (
          <div
            className="absolute top-0 right-0 bottom-0 cursor-col-resize group z-30"
            style={{ width: "12px", marginRight: "-6px" }}
            onMouseDown={onStartResize}
          >
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-1 h-16 bg-slate-300 group-hover:bg-blue-500 rounded-l transition-colors" />
          </div>
        )}

        <FilterPanel
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          filters={filters}
          onChange={onFilterChange}
          activeTab={activeTab}
          onSearch={() => {
            onFilterSearch?.()
            setShowFilters(false)
          }}
          onReset={onFilterReset}
          departments={departments}
          messagethemes={messagethemes}
          isAdmin={isAdmin}
        />
      </motion.div>
    </div>
  )
}

export default ChatListSidebar
